import { planYearOverride } from '../../api/library/planYearOverride';
// planCatalog — plan lookup backed by pm_plans + pm_plan_benefits via
// the /api/plans serverless route. Replaces the 12-plan static array
// in cmsPlans.ts for Step 5 Benefit Filters and Step 6 Quote &
// Delivery renders.
//
// The shape returned matches the Plan interface in src/types/plans.ts
// so downstream consumers (computeFunnel, SideBySideTable, benefit
// cards) don't need to change. Graceful-degradation fallback: if the
// server route errors, callers can fall back to the static cmsPlans
// array so the UI still renders during a Supabase outage.

import type { CostShare, OutOfNetwork, Plan, PlanBenefits } from '@/types/plans';
import type { PlanType, StateCode } from '@/types/session';

interface ApiPlan {
  id: string;
  non_commissionable?: boolean;
  contract_id: string;
  plan_number: string;
  segment_id: string;
  carrier: string;
  plan_name: string;
  state: string;
  counties: string[];
  plan_type: PlanType;
  plan_shape: string | null;
  snp_type: string | null;
  // Landscape-sourced SNP details. Optional on the wire so older
  // /api/plans builds without migration 014 still deserialize —
  // toPlan() coalesces missing/undefined to null / false.
  dsnp_integration_status?: string | null;
  zero_cost_sharing?: boolean;
  csnp_condition_type?: string | null;
  // CMS-SNP-report-sourced D-SNP fields (migration 015). Same
  // optional-wire treatment for graceful degradation.
  dsnp_accepted_populations?: string[] | null;
  // Per-plan manual override from pm_dsnp_populations. Threaded
  // through by /api/plans (attachManualDsnpPopulations) when a match
  // exists; older builds and static fixtures don't emit it.
  dsnp_accepted_populations_manual?: string[] | null;
  dsnp_only_contract?: boolean | null;
  premium: number;
  // Member-payable premium (D-SNP → $0 via LIS, else equals premium).
  // Older /api/plans builds didn't return this; toPlan() falls back to
  // a snp_type-aware derivation so the consumer surface stays correct.
  consumer_premium?: number;
  annual_deductible: number | null;
  moop_in_network: number;
  moop_out_of_network: number | null;
  drug_deductible: number | null;
  has_drug_coverage: boolean;
  part_b_giveback: number;
  star_rating: number;
  // Medicare.gov Plan Compare deep-link for the plan's Summary of
  // Benefits. Server always populates; toPlan() falls back to a
  // synthesized URL for older builds that don't return the field.
  sbf_url?: string;
  benefits: Partial<PlanBenefits>;
  out_of_network?: OutOfNetwork | null;
  formulary?: Record<string, never>;
  in_network_npis?: string[];
}

interface ApiPlansResponse {
  plans: ApiPlan[];
  source: 'pm_plans' | 'static_fallback';
  /** Server-side flag: true when the row cap was hit and the pool is a
   *  partial county universe. Absent on older builds; treat as false. */
  truncated?: boolean;
}

export interface FetchPlansParams {
  state: StateCode | null;
  county: string;
  planType: PlanType | null;
  /** Pass explicit ids to refetch a known finalist set (Step 6). */
  ids?: string[];
  /** Optional abort signal — cancel this fetch when the caller re-fires
   *  with newer inputs. Prevents a stale response from overwriting a
   *  newer eligiblePlans state (the AgentBase-hydrate race). */
  signal?: AbortSignal;
  /** Agent Providers screen only: also return plans Rob can't sell
   *  (tagged non_commissionable) so doctors are checked across every
   *  carrier. Leave unset everywhere else. */
  includeNonCommissionable?: boolean;
}

/** Missing-geo hard-fail. Callers MUST guard on state+county being
 *  non-empty before invoking fetchPlansForClient — the server now
 *  rejects empty geo with 400 (previously it silently returned a
 *  statewide truncated slice). This subclass lets callers distinguish
 *  a guard failure from a real fetch error. See Phase 1.10 diagnosis. */
export class MissingGeoError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'MissingGeoError';
  }
}

// Filled in on every fetch so callers can cheaply ask "did the last
// lookup actually hit the database?" without wiring a separate status
// channel through React Query. `'static_fallback'` means the server
// errored and we returned the 12-plan const below.
let lastSource: ApiPlansResponse['source'] = 'pm_plans';
let lastTruncated = false;

export function lastPlanSource(): ApiPlansResponse['source'] {
  return lastSource;
}

/** True when the most recent fetch reported the row cap was hit. Bench
 *  UI reads this to render the partial-pool banner. */
export function lastPlanTruncated(): boolean {
  return lastTruncated;
}

export async function fetchPlansForClient(params: FetchPlansParams): Promise<Plan[]> {
  const usingIds = Boolean(params.ids && params.ids.length > 0);
  // Guard: refuse to fire without complete geo unless the caller passed
  // ids. The server enforces the same rule; guarding client-side
  // suppresses the pointless request and gives us a typed error the
  // caller can react to differently than a network failure.
  if (!usingIds) {
    if (!params.state) {
      throw new MissingGeoError('state is required');
    }
    if (!params.county || !params.county.trim()) {
      throw new MissingGeoError('county is required');
    }
  }

  const qs = new URLSearchParams();
  if (usingIds) {
    qs.set('ids', params.ids!.join(','));
  } else {
    qs.set('state', params.state!);
    qs.set('county', params.county);
    if (params.planType) qs.set('planType', params.planType);
    if (params.includeNonCommissionable) qs.set('includeNonCommissionable', '1');
  }
  qs.set('limit', '2000');
  // Forward the ?plan_year= override so the rows we get back are the same
  // catalog year the Part D constants resolve to. Absent => no param => the
  // API's own date-driven resolver decides, unchanged.
  const yearOverride = planYearOverride();
  if (yearOverride !== null) qs.set('plan_year', String(yearOverride));

  try {
    const res = await fetch(`/api/plans?${qs.toString()}`, {
      method: 'GET',
      headers: { Accept: 'application/json' },
      signal: params.signal,
    });
    if (!res.ok) {
      const text = await res.text();
      throw new Error(`plans ${res.status} — ${text.slice(0, 200)}`);
    }
    const body = (await res.json()) as ApiPlansResponse;
    lastSource = body.source ?? 'pm_plans';
    lastTruncated = body.truncated === true;
    if (lastTruncated) {
      console.warn(
        `[planCatalog] server truncated: ${body.plans.length} plans returned at ` +
          `row cap for state=${params.state} county=${params.county}. Bench pool is a ` +
          `partial county universe — check /api/plans limit or query filters.`,
      );
    }
    return body.plans.map(toPlan);
  } catch (err) {
    // Re-throw guard errors so the caller can distinguish "we refused to
    // fetch because geo is incomplete" from "the fetch failed and we're
    // falling back to seeds". Guard errors must NEVER trigger the static
    // fallback — that would just paper over the missing geo.
    if (err instanceof MissingGeoError) throw err;
    // AbortError shouldn't fall back either — the caller cancelled us,
    // typically because a newer fetch is in flight. Return an empty
    // pool so the newer response wins deterministically.
    if (err instanceof DOMException && err.name === 'AbortError') {
      return [];
    }
    console.warn('[planCatalog] fetch failed, falling back to static seed:', err);
    lastSource = 'static_fallback';
    lastTruncated = false;
    const { fallbackPlansForClient } = await import('./cmsPlans');
    return fallbackPlansForClient(params);
  }
}

/** Convenience for Step 6: fetch a specific set of finalist ids. */
export async function fetchPlansByIds(ids: string[]): Promise<Plan[]> {
  if (ids.length === 0) return [];
  return fetchPlansForClient({ state: null, county: '', planType: null, ids });
}

// Summary-of-Benefits fallback — mirrors api/plans.ts planFinderUrl()
// so older /api/plans builds that don't return sbf_url still produce
// a working link client-side. See api/plans.ts for why we build a
// Google search URL instead of a medicare.gov deep-link. Keep
// PLAN_YEAR in sync.
const PLAN_YEAR = 2026;
function fallbackSbfUrl(id: string): string {
  const [contractId, planId, segmentId] = id.split('-');
  const seg = (segmentId ?? '000').padStart(3, '0');
  const q = `"${contractId}-${planId}-${seg}" "Summary of Benefits" ${PLAN_YEAR} filetype:pdf`;
  return `https://www.google.com/search?q=${encodeURIComponent(q)}`;
}

function toPlan(p: ApiPlan): Plan {
  return {
    id: p.id,
    contract_id: p.contract_id,
    plan_number: p.plan_number,
    carrier: p.carrier,
    plan_name: p.plan_name,
    ...(p.non_commissionable ? { non_commissionable: true } : {}),
    state: p.state as StateCode,
    counties: p.counties ?? [],
    plan_type: p.plan_type,
    // Older /api/plans builds didn't return plan_shape; fall back to
    // null so HMO/PPO filters simply miss instead of crashing the
    // toUpperCase() call downstream.
    plan_shape: p.plan_shape ?? null,
    snp_type: p.snp_type ?? null,
    dsnp_integration_status: p.dsnp_integration_status ?? null,
    zero_cost_sharing: p.zero_cost_sharing === true,
    csnp_condition_type: p.csnp_condition_type ?? null,
    dsnp_accepted_populations: p.dsnp_accepted_populations ?? null,
    dsnp_accepted_populations_manual: p.dsnp_accepted_populations_manual ?? null,
    dsnp_only_contract: p.dsnp_only_contract ?? null,
    premium: p.premium ?? 0,
    // Prefer the server's computed consumer_premium. Fallback derivation
    // mirrors api/plans.ts (D-SNP → 0, else premium) so older builds
    // still surface the right value.
    consumer_premium:
      p.consumer_premium ?? (p.snp_type === 'D-SNP' ? 0 : (p.premium ?? 0)),
    // Coalesce NULL → 0. pm_plans treats "no medical deductible" as
    // NULL, but consumers should see $0. Audit suite showed 176 RED
    // diffs against Medicare.gov, every one a NULL-vs-$0 leak.
    annual_deductible: p.annual_deductible ?? 0,
    moop_in_network: p.moop_in_network ?? 0,
    moop_out_of_network: p.moop_out_of_network ?? null,
    drug_deductible: p.drug_deductible ?? null,
    // Older /api/plans builds didn't return has_drug_coverage; treat
    // them as covered to avoid false-positive VA matches.
    has_drug_coverage: p.has_drug_coverage ?? true,
    part_b_giveback: p.part_b_giveback ?? 0,
    star_rating: p.star_rating ?? 0,
    sbf_url: p.sbf_url ?? fallbackSbfUrl(p.id),
    benefits: fillBenefits(p.benefits ?? {}),
    out_of_network: p.out_of_network ?? null,
    // Formulary is queried per-(plan, rxcui) via /api/formulary — the
    // Plan type still carries a formulary dict for back-compat; we
    // leave it empty so any direct lookup misses and the code falls
    // through to the proper formularyLookup path.
    formulary: {},
    // Vestigial. Per-plan network status lives in
    // useSession.providers[*].networkStatus[planId], written by the
    // hydration effect in AgentV3App via checkNetworkBatch. BenchCard
    // and useBenchFilters read from that store directly.
    in_network_npis: [],
  };
}

function fillBenefits(partial: Partial<PlanBenefits>): PlanBenefits {
  return {
    dental: {
      preventive: partial.dental?.preventive ?? false,
      comprehensive: partial.dental?.comprehensive ?? false,
      annual_max: partial.dental?.annual_max ?? 0,
      description: partial.dental?.description ?? null,
      // Per-category routine dental drill-down (api/plans benefits.dental.detail,
      // pm_plan_benefits.detail jsonb). Undefined/null when not filed → the
      // dental detail table renders a single "Not filed — call to verify" line.
      detail: partial.dental?.detail ?? null,
    },
    vision: {
      exam: partial.vision?.exam ?? false,
      eyewear_allowance_year: partial.vision?.eyewear_allowance_year ?? 0,
      description: partial.vision?.description ?? null,
    },
    hearing: {
      aid_allowance_year: partial.hearing?.aid_allowance_year ?? 0,
      exam: partial.hearing?.exam ?? false,
      description: partial.hearing?.description ?? null,
    },
    transportation: {
      rides_per_year: partial.transportation?.rides_per_year ?? 0,
      distance_miles: partial.transportation?.distance_miles ?? 0,
      description: partial.transportation?.description ?? null,
    },
    otc: {
      allowance_per_quarter: partial.otc?.allowance_per_quarter ?? 0,
      description: partial.otc?.description ?? null,
    },
    food_card: {
      allowance_per_month: partial.food_card?.allowance_per_month ?? 0,
      restricted_to_medicaid_eligible:
        partial.food_card?.restricted_to_medicaid_eligible ?? false,
      description: partial.food_card?.description ?? null,
    },
    diabetic: {
      covered: partial.diabetic?.covered ?? false,
      preferred_brands: partial.diabetic?.preferred_brands ?? [],
    },
    fitness: {
      enabled: partial.fitness?.enabled ?? false,
      program: partial.fitness?.program ?? null,
    },
    medical: {
      primary_care: cs(partial.medical?.primary_care),
      specialist: cs(partial.medical?.specialist),
      urgent_care: cs(partial.medical?.urgent_care),
      emergency: cs(partial.medical?.emergency),
      inpatient: cs(partial.medical?.inpatient),
      mental_health_inpatient: cs(partial.medical?.mental_health_inpatient),
      snf: cs(partial.medical?.snf),
      outpatient_surgery_hospital: cs(partial.medical?.outpatient_surgery_hospital),
      outpatient_surgery_asc: cs(partial.medical?.outpatient_surgery_asc),
      outpatient_observation: cs(partial.medical?.outpatient_observation),
      lab_services: cs(partial.medical?.lab_services),
      diagnostic_procedures: cs(partial.medical?.diagnostic_procedures),
      xray: cs(partial.medical?.xray),
      advanced_imaging: cs(partial.medical?.advanced_imaging),
      mental_health_individual: cs(partial.medical?.mental_health_individual),
      mental_health_group: cs(partial.medical?.mental_health_group),
      physical_speech_therapy: cs(partial.medical?.physical_speech_therapy),
      occupational_therapy: cs(partial.medical?.occupational_therapy),
      telehealth: cs(partial.medical?.telehealth),
      dental_medicare_covered: cs(partial.medical?.dental_medicare_covered),
      ambulance: cs(partial.medical?.ambulance),
      air_transportation: cs(partial.medical?.air_transportation),
      chiropractic: cs(partial.medical?.chiropractic),
      acupuncture: cs(partial.medical?.acupuncture),
      podiatry: cs(partial.medical?.podiatry),
      substance_abuse: cs(partial.medical?.substance_abuse),
      dme_prosthetics: cs(partial.medical?.dme_prosthetics),
      partb_drugs: cs(partial.medical?.partb_drugs),
      diabetic_supplies: cs(partial.medical?.diabetic_supplies),
      insulin: cs(partial.medical?.insulin),
      home_health: cs(partial.medical?.home_health),
      renal_dialysis: cs(partial.medical?.renal_dialysis),
    },
    rx_tiers: {
      tier_1: cs(partial.rx_tiers?.tier_1),
      tier_2: cs(partial.rx_tiers?.tier_2),
      tier_3: cs(partial.rx_tiers?.tier_3),
      tier_4: cs(partial.rx_tiers?.tier_4),
      tier_5: cs(partial.rx_tiers?.tier_5),
    },
  };
}

// Carry the CMS range (copay_low/high, coinsurance_low/high) and the
// alternate filing through. Dropping them here is why Compare showed a flat
// "$0" for ranged rows whose description didn't spell the range out.
function cs(v: Partial<CostShare> | undefined): CostShare {
  return {
    copay: v?.copay ?? null,
    coinsurance: v?.coinsurance ?? null,
    description: v?.description ?? null,
    copay_low: v?.copay_low ?? null,
    copay_high: v?.copay_high ?? null,
    coinsurance_low: v?.coinsurance_low ?? null,
    coinsurance_high: v?.coinsurance_high ?? null,
    alt_copay: v?.alt_copay ?? null,
    alt_coinsurance: v?.alt_coinsurance ?? null,
    alt_source: v?.alt_source ?? null,
  };
}
